import { DiscrepancyMasterDetail } from "@/components/reconciliation/result/DiscrepancyMasterDetail";
import { DiscrepancyVirtualList } from "@/components/reconciliation/result/DiscrepancyVirtualList";
import { EmptyState } from "@/components/reconciliation/result/EmptyState";
import { FailedState } from "@/components/reconciliation/result/FailedState";
import { LimitExceededBanner } from "@/components/reconciliation/result/LimitExceededBanner";
import { ResultBanner, type ResultLayout } from "@/components/reconciliation/result/ResultBanner";
import { ResultFilterBar } from "@/components/reconciliation/result/ResultFilterBar";
import { StaleOverlay } from "@/components/reconciliation/result/StaleOverlay";
import { errorMessageFromRun } from "@/components/reconciliation/result/lib/error-message";
import { useDiscrepancyKbd } from "@/components/reconciliation/result/lib/useDiscrepancyKbd";
import {
	type RunReportViewModel,
	toRunReportViewModel,
} from "@/components/reconciliation/result/lib/view-model";
import { usePinnedDiscrepancies } from "@/hooks/use-pinned-discrepancies";
import { usePrintMode } from "@/hooks/use-print-mode";
import { useReportFilters } from "@/hooks/use-report-filters";
import { getRunReport, getRunsList } from "@/services/api";
import { reconciliationMessages, reconciliationResultMessages } from "@/services/messages";
import { useTabActions, useTabStore } from "@/stores/tab-store";
import { useQuery } from "@tanstack/react-query";
import type { Virtualizer } from "@tanstack/react-virtual";
import { Loader2 } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";

const MASTER_DETAIL_THRESHOLD = 15;

/**
 * Главная страница отчёта сверки (FR-008, US1+US2+US3).
 *
 * Discriminated rendering:
 * - loading → spinner
 * - not-found (404) → текст «Отчёт не найден»
 * - error (TanStack Query всё провалил) → inline + manual retry (FR-028c)
 * - limit-exceeded → LimitExceededBanner (FR-023)
 * - ok + run.status === "failed" → FailedState с retry-цепочкой (FR-020/FR-020a)
 * - ok + cards.length === 0 → EmptyState (FR-019)
 * - ok + cards.length > 0 → ResultBanner + ResultFilterBar + DiscrepancyVirtualList
 *
 * Stale (FR-021, R4): если `tab.stale === true`, контент обёрнут в
 * `pointer-events-none opacity-60` wrapper, а сверху — sticky StaleOverlay.
 * StaleOverlay подгружает `actualRunId` через `getRunsList` (первый не-stale).
 */
export function ReconciliationResultPage() {
	const tab = useTabStore((state) => {
		const active = state.tabs.find((t) => t.id === state.activeTabId);
		return active?.type === "reconciliation-result" ? active : undefined;
	});
	const { closeReconciliationResultTab } = useTabActions();

	const runId = tab?.runId;
	const setId = tab?.setId;
	const isStale = tab?.stale === true;

	const { data, error, refetch, isFetching } = useQuery({
		queryKey: ["run-report", runId],
		queryFn: () => getRunReport(runId as string),
		enabled: runId !== undefined,
		staleTime: Number.POSITIVE_INFINITY,
		gcTime: 5 * 60 * 1000,
		refetchOnWindowFocus: false,
		// FR-028c: 3 авто-ретрая с exp backoff (1s/2s/4s).
		// Override глобального `retry: 1` из main.tsx.
		retry: 3,
	});

	// Для stale-overlay подгружаем актуальный runId (первый не-stale run).
	const { data: runsList } = useQuery({
		queryKey: ["recon-runs", setId],
		queryFn: () => getRunsList(setId as number),
		enabled: isStale && setId !== undefined,
		staleTime: 0,
		refetchOnWindowFocus: true,
		retry: 3, // FR-028c
	});
	const actualRunId =
		isStale && runsList ? (runsList.find((r) => r.id !== runId)?.id ?? null) : null;

	const vm: RunReportViewModel = toRunReportViewModel(data ?? null, error);
	const cards = vm.status === "ok" ? vm.cards : [];

	const filters = useReportFilters({ cards });
	const virtualizerRef = useRef<Virtualizer<HTMLDivElement, Element> | null>(null);
	const filterSearchInputRef = useRef<HTMLInputElement | null>(null);
	const isPrint = usePrintMode();
	const [manualLayout, setManualLayout] = useState<ResultLayout | null>(null);
	const pinnedHook = usePinnedDiscrepancies(runId);

	// Sprint 5 / B2: layout считается заранее (до useDiscrepancyKbd), чтобы
	// можно было гасить hook в master-detail режиме — там собственный
	// useDiscrepancyKbd внутри DiscrepancyMasterDetail. Без guard'а оба
	// listener'а ловили бы `/` и фокусировали разные search input'ы.
	const autoLayout: ResultLayout =
		cards.length >= MASTER_DETAIL_THRESHOLD ? "master-detail" : "feed";
	const layout: ResultLayout = isPrint ? "feed" : (manualLayout ?? autoLayout);

	// Q9: «/» работает глобально (фокусирует ResultFilterBar в feed-mode).
	// J/K/Esc — только master-detail. В master-detail режиме hook отключаем —
	// MasterDetail сам регистрирует свой listener (T-B2.1, T-B2.2).
	useDiscrepancyKbd({
		activeId: null,
		orderedIds: [],
		onActiveChange: () => undefined,
		onSearchFocus: () => filterSearchInputRef.current?.focus(),
		feedOnly: true,
		disabled: layout === "master-detail",
	});

	const handleRelatedClick = (targetId: string) => {
		const visibleInFiltered = filters.filteredCards.some((c) => c.id === targetId);
		if (visibleInFiltered) {
			filters.scrollToDiscrepancy(targetId, virtualizerRef.current);
			filters.highlightDiscrepancy(targetId);
			return;
		}
		const inFullList = cards.some((c) => c.id === targetId);
		if (!inFullList) return;
		const drill = reconciliationResultMessages.drill;
		toast(drill.target_hidden_toast, {
			action: {
				label: drill.target_hidden_cta,
				onClick: () => {
					filters.resetFilters();
					setTimeout(() => {
						filters.scrollToDiscrepancy(targetId, virtualizerRef.current);
						filters.highlightDiscrepancy(targetId);
					}, 0);
				},
			},
		});
	};

	const handleClose = () => {
		if (runId) closeReconciliationResultTab(runId);
	};

	if (!runId || !tab || setId === undefined) {
		return null;
	}

	const content = renderContent();

	return (
		<>
			{isStale && (
				<StaleOverlay
					currentSetName={tab.title}
					actualRunId={actualRunId}
					currentSetId={setId}
					onOpenActual={() => {
						/* no-op: actualRunId уже задан, openReconciliationResultTab вызывается внутри StaleOverlay */
					}}
				/>
			)}
			{/*
			 * Sprint 5 / B3: stale-overlay использует HTML `inert` атрибут вместо
			 * `pointer-events-none`. inert надёжнее: блокирует focus/keyboard
			 * (Tab, screen readers) дополнительно к click events. Полифилл для
			 * старых браузеров — `aria-hidden=true` (caniuse: inert поддержан в
			 * Safari 15.5+, Chrome 102+, Firefox 112+ — на Mac mini production
			 * браузеры точно покрывают). tabIndex={-1} применять не нужно — inert
			 * автоматически вычеркивает элемент из focus-tree.
			 */}
			<div
				inert={isStale ? true : undefined}
				aria-hidden={isStale ? "true" : undefined}
				className={isStale ? "select-none opacity-60" : undefined}
			>
				{content}
			</div>
		</>
	);

	function renderContent() {
		if (vm.status === "loading") {
			return (
				<div className="flex items-center justify-center py-16 text-sm text-slate-500">
					<Loader2 className="mr-2 h-4 w-4 animate-spin" /> {reconciliationMessages.loading}
				</div>
			);
		}

		if (vm.status === "not-found") {
			return (
				<div className="mx-auto max-w-3xl rounded-lg border border-slate-200 bg-white p-6 text-center">
					<p className="text-sm text-slate-700">{reconciliationMessages.error_run_not_found}</p>
				</div>
			);
		}

		if (vm.status === "error") {
			return (
				<div className="mx-auto max-w-3xl rounded-lg border border-red-200 bg-red-50 p-4">
					<p className="text-sm text-red-800">{reconciliationMessages.toast_api_error}</p>
					<button
						type="button"
						onClick={() => refetch()}
						disabled={isFetching}
						className="mt-3 inline-flex items-center rounded-lg border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
					>
						{isFetching && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
						{reconciliationMessages.button_retry}
					</button>
				</div>
			);
		}

		if (vm.status === "limit-exceeded") {
			return (
				<LimitExceededBanner
					totalCount={vm.totalCount}
					severityBreakdown={vm.severityBreakdown}
					setName={vm.setName}
					onClose={handleClose}
				/>
			);
		}

		// vm.status === "ok"
		const setName = vm.report.run.parametersSnapshot.set.name;

		// FR-020: failed run → fullscreen FailedState с retry-цепочкой.
		if (vm.report.run.status === "failed") {
			return (
				<FailedState
					setId={vm.report.run.setId}
					setName={setName}
					runId={vm.report.run.id}
					errorMessage={errorMessageFromRun(vm.report.run.errorMessage)}
					onClose={handleClose}
				/>
			);
		}

		// FR-019: empty-state «0 расхождений».
		if (cards.length === 0) {
			return <EmptyState run={vm.report.run} setName={setName} onClose={handleClose} />;
		}

		const filterMessages = reconciliationResultMessages.filter;
		const isEmptyAfterFilter = filters.isFiltering && filters.filteredCards.length === 0;

		// Sprint 5 / B1: client-side mute убран целиком. visibleCards теперь
		// эквивалентен filters.filteredCards — но оставляем алиас, чтобы не
		// размазывать ниже и сохранить семантический смысл «после ВСЕХ фильтров».
		const visibleCards = filters.filteredCards;
		const hasAnyFilter = filters.isFiltering || visibleCards.length !== cards.length;

		// Sprint 5 / B2: layout вычислен на уровне функции выше (для
		// useDiscrepancyKbd-guard). Здесь просто используем готовое значение.
		// Toggle показываем всегда (кроме печати) — пользователь сам решает, какой
		// вид удобнее, даже на коротких отчётах.
		const layoutToggleVisible = !isPrint;

		return (
			<div className="mx-auto max-w-7xl space-y-4 p-2">
				<ResultBanner
					run={vm.report.run}
					setName={setName}
					counters={vm.counters}
					shownCount={hasAnyFilter ? visibleCards.length : undefined}
					layout={layoutToggleVisible ? layout : undefined}
					onLayoutChange={layoutToggleVisible ? setManualLayout : undefined}
				/>

				<ResultFilterBar
					searchQuery={filters.searchQuery}
					isFiltering={filters.isFiltering}
					onSearchChange={filters.setSearchQuery}
					onReset={filters.resetFilters}
					searchInputRef={filterSearchInputRef}
				/>

				{isEmptyAfterFilter ? (
					<div className="rounded-lg border border-slate-200 bg-white p-6 text-center">
						<p className="text-sm font-medium text-slate-700">
							{filterMessages.empty_filtered_title}
						</p>
						<button
							type="button"
							onClick={filters.resetFilters}
							className="mt-3 inline-flex items-center rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
						>
							{filterMessages.empty_filtered_cta}
						</button>
					</div>
				) : layout === "master-detail" ? (
					<DiscrepancyMasterDetail
						cards={filters.filteredCards}
						runId={runId}
						pinned={pinnedHook}
						onRelatedClick={handleRelatedClick}
					/>
				) : (
					<DiscrepancyVirtualList
						cards={visibleCards}
						highlightedDiscrepancyId={filters.highlightedDiscrepancyId}
						virtualizerRef={virtualizerRef}
						onRelatedClick={handleRelatedClick}
						runId={runId}
					/>
				)}
			</div>
		);
	}
}
